import React, { useState } from "react";
import AppShell from "@/components/AppShell";
import { useLang } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { Users, UserPlus, Mail, Send, Shield, Crown } from "lucide-react";
import { toast } from "sonner";

export default function FamilyVault() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [invites, setInvites] = useState(() => {
    try { return JSON.parse(localStorage.getItem("s2s_invites") || "[]"); }
    catch { return []; }
  });
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("guardian");

  const sendInvite = (e) => {
    e.preventDefault();
    if (!email) return;
    const next = [...invites, { email, role, sent_at: new Date().toISOString(), status: "pending" }];
    setInvites(next);
    localStorage.setItem("s2s_invites", JSON.stringify(next));
    setEmail("");
    toast.success(lang === "pt" ? "Convite enviado" : "Invite sent", {
      description: `${email} · ${role}`,
    });
  };

  const remove = (i) => {
    const next = invites.filter((_, idx) => idx !== i);
    setInvites(next);
    localStorage.setItem("s2s_invites", JSON.stringify(next));
  };

  return (
    <AppShell>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-overline flex items-center gap-2"><Users size={12} /> Constellation</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-2" data-testid="family-title">{t("family.title")}</h1>
          <p className="text-white/55 mt-2 max-w-2xl">{t("family.subtitle")}</p>
        </div>
        <div className="holo-panel px-4 py-3 flex items-center gap-3">
          <Crown size={14} className="text-[#00D4FF]" />
          <div>
            <div className="label-overline">{lang === "pt" ? "Comandante" : "Commander"}</div>
            <div className="font-mono-data text-sm text-white">{user?.name}</div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 holo-panel p-6">
          <div className="label-overline mb-4 flex items-center gap-2">
            <UserPlus size={12} /> {lang === "pt" ? "Convidar Membro" : "Invite Family Member"}
          </div>
          <form onSubmit={sendInvite} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="label-overline">{lang === "pt" ? "Email" : "Email"}</label>
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="family-invite-email"
                  placeholder="constellation@s2s.space"
                  className="w-full mt-2 bg-transparent border-b border-[#00D4FF]/25 focus:border-[#00D4FF] outline-none py-2 text-white"
                />
              </div>
              <div>
                <label className="label-overline">{lang === "pt" ? "Papel" : "Role"}</label>
                <select
                  value={role} onChange={(e) => setRole(e.target.value)}
                  data-testid="family-invite-role"
                  className="w-full mt-2 bg-black/30 border border-[#00D4FF]/20 focus:border-[#00D4FF] outline-none py-2 px-3 text-white"
                >
                  <option value="guardian">{lang === "pt" ? "Guardião" : "Guardian"}</option>
                  <option value="contributor">{lang === "pt" ? "Contribuinte" : "Contributor"}</option>
                  <option value="viewer">{lang === "pt" ? "Visualizador" : "Viewer"}</option>
                </select>
              </div>
            </div>
            <button type="submit" className="btn-primary-s2s !py-3 !text-[11px]" data-testid="family-invite-send">
              <Send size={14} /> {lang === "pt" ? "Enviar Convite" : "Send Invitation"}
            </button>
          </form>

          {invites.length > 0 && (
            <div className="mt-8" data-testid="family-invites-list">
              <div className="label-overline mb-3">{lang === "pt" ? "Convites Enviados" : "Sent Invitations"}</div>
              <div className="space-y-2">
                {invites.map((inv, i) => (
                  <div key={i} className="flex items-center justify-between border border-white/5 p-3 hover:border-white/15 transition-colors">
                    <div className="flex items-center gap-3">
                      <Mail size={14} className="text-[#00D4FF]" />
                      <div>
                        <div className="text-sm text-white">{inv.email}</div>
                        <div className="font-mono-data text-[10px] tracking-[0.16em] uppercase text-white/55 mt-0.5">{inv.role} · {inv.status}</div>
                      </div>
                    </div>
                    <button onClick={() => remove(i)} className="font-mono-data text-[10px] text-white/40 hover:text-[#FF3366] uppercase tracking-[0.18em]">
                      {lang === "pt" ? "Remover" : "Remove"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="holo-panel p-5">
            <Shield size={18} className="text-[#00D4FF]" />
            <div className="label-overline mt-3">{lang === "pt" ? "Permissões" : "Permission Tiers"}</div>
            <ul className="mt-3 space-y-3 text-sm text-white/75">
              <li><span className="text-white font-semibold">{lang === "pt" ? "Guardião" : "Guardian"}</span> — {lang === "pt" ? "Acesso total às cápsulas familiares" : "Full access to family capsules"}</li>
              <li><span className="text-white font-semibold">{lang === "pt" ? "Contribuinte" : "Contributor"}</span> — {lang === "pt" ? "Pode adicionar conteúdo" : "Can add content"}</li>
              <li><span className="text-white font-semibold">{lang === "pt" ? "Visualizador" : "Viewer"}</span> — {lang === "pt" ? "Apenas leitura" : "Read-only access"}</li>
            </ul>
          </div>
          <div className="holo-panel p-5">
            <div className="label-overline">{lang === "pt" ? "Capacidades" : "Family Capabilities"}</div>
            <ul className="mt-3 space-y-2 text-sm text-white/70 leading-relaxed">
              <li>· {lang === "pt" ? "Cápsulas familiares compartilhadas" : "Shared family capsules"}</li>
              <li>· {lang === "pt" ? "Linha do tempo geracional" : "Multigenerational timeline"}</li>
              <li>· {lang === "pt" ? "Cofre de legado memorial" : "Memorial legacy vault"}</li>
              <li>· {lang === "pt" ? "Co-assinatura de transmissões" : "Co-signed transmissions"}</li>
            </ul>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
